import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logger from '../services/logger';
import { Z } from '../lib/zIndex';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const PWAInstallPrompt = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if app is already installed (running in standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      (window.navigator as { standalone?: boolean }).standalone === true;
    setIsStandalone(standalone);

    // Check if device is iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as { MSStream?: unknown }).MSStream;
    setIsIOS(ios);

    // Only show on mobile devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (!isMobile) {
      return;
    }

    // Check if user has previously dismissed the prompt
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedDate = dismissed ? new Date(dismissed) : null;
    const daysSinceDismissed = dismissedDate ?
      (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24) : Infinity;

    // Don't show if already installed, dismissed recently (within 7 days), or not mobile
    if (standalone || (dismissedDate && daysSinceDismissed < 7)) {
      return;
    }

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show the prompt after a short delay
      setTimeout(() => setShowPrompt(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show custom prompt if not installed and not recently dismissed
    if (ios && !standalone && daysSinceDismissed >= 7) {
      setTimeout(() => setShowPrompt(true), 2000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt && !isIOS) {
      return;
    }

    if (deferredPrompt) {
      // Android/Chrome installation
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        logger.debug('User accepted the install prompt');
      } else {
        logger.debug('User dismissed the install prompt');
      }

      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  if (!showPrompt || isStandalone) {
    return null;
  }

  return (
    <div className={`fixed bottom-4 start-4 end-4 z-[${Z.toast}] md:start-auto md:end-4 md:max-w-sm`}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-slate-200 dark:border-gray-700 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <img src="/favicon.svg" alt="App icon" className="w-12 h-12 rounded-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              {t('pwa.installTitle')}
            </h3>
            <p className="text-xs text-slate-600 dark:text-gray-400 mt-1">
              {isIOS
                ? t('pwa.installDescriptionIOS')
                : t('pwa.installDescriptionAndroid')
              }
            </p>
            {!isIOS && deferredPrompt && (
              <button
                onClick={handleInstallClick}
                className="mt-3 w-full bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors"
              >
                {t('pwa.installButton')}
              </button>
            )}
            {isIOS && (
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-600 dark:text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/>
                  <polyline points="7 9 12 4 17 9"/>
                  <line x1="12" y1="4" x2="12" y2="16"/>
                </svg>
                <span>{t('pwa.installInstructionsIOS')}</span>
              </div>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-gray-300"
            aria-label={t('pwa.dismiss')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
